import * as React from "react";
import {
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Text,
} from "@fluentui/react-components";
import {
  Globe24Regular,
  Checkmark24Regular,
  ChevronRight20Regular
} from "@fluentui/react-icons";
import { useTranslation } from 'react-i18next';

interface LanguageSelectorProps {
  selectedLanguage: string;
  onLanguageChange: (langCode: string) => void;
}

const fullLanguageCodes = [
  'en-US', 'de-DE', 'es-ES', 'fr-FR', 'hi-IN',
  'it-IT', 'ja-JP', 'ko-KR', 'pt-PT', 'ru-RU',
  'uk-UA', 'zh-CN', 'zh-HK'
];

const getLanguageIcon = (code: string) => {
  const lang = code.split('-')[0];
  const icons: Record<string, React.ReactElement> = {
    'en': <Globe24Regular />,
    'de': <span style={{ fontSize: '16px' }}>🇩🇪</span>,
    'es': <span style={{ fontSize: '16px' }}>🇪🇸</span>,
    'fr': <span style={{ fontSize: '16px' }}>🇫🇷</span>,
    'hi': <span style={{ fontSize: '16px' }}>🇮🇳</span>,
    'it': <span style={{ fontSize: '16px' }}>🇮🇹</span>,
    'ja': <span style={{ fontSize: '16px' }}>🇯🇵</span>,
    'ko': <span style={{ fontSize: '16px' }}>🇰🇷</span>,
    'pt': <span style={{ fontSize: '16px' }}>🇵🇹</span>,
    'ru': <span style={{ fontSize: '16px' }}>🇷🇺</span>,
    'uk': <span style={{ fontSize: '16px' }}>🇺🇦</span>,
    'zh': <span style={{ fontSize: '16px' }}>🇨🇳</span>,
  };
  return icons[lang] || <Globe24Regular />;
};

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  selectedLanguage,
  onLanguageChange,
}) => {
  const { t } = useTranslation();

  const getLanguageDisplayName = (code: string): string => {
    return t(`languages.${code}`, { defaultValue: code });
  };

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button 
          appearance="outline" 
          icon={<Globe24Regular />}
          style={{ minWidth: '160px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Text>{getLanguageDisplayName(selectedLanguage)}</Text>
            <ChevronRight20Regular />
          </div>
        </Button>
      </MenuTrigger>
      <MenuPopover style={{ maxHeight: '300px', overflowY: 'auto' }}>
        <MenuList>
          {fullLanguageCodes.map((langCode) => {
            const isActive = selectedLanguage === langCode;
            return (
              <MenuItem
                key={langCode}
                icon={getLanguageIcon(langCode)}
                onClick={() => onLanguageChange(langCode)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Text>{getLanguageDisplayName(langCode)}</Text>
                  {isActive && <Checkmark24Regular />}
                </div>
              </MenuItem>
            );
          })}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export default LanguageSelector;